import { Line, LineChart, ResponsiveContainer } from 'recharts';
import type { ITrendPoint } from './TrendLineChart';

interface ITrendSparklineProps {
  data: ITrendPoint[];
  /** Which issue type to sparkline. 'all' sums all three. */
  activeType: 'all' | 'story' | 'bug' | 'task';
}

function totalForPoint(p: ITrendPoint, activeType: ITrendSparklineProps['activeType']): number {
  if (activeType === 'all') return p.Story + p.Bug + p.Task;
  if (activeType === 'story') return p.Story;
  if (activeType === 'bug') return p.Bug;
  return p.Task;
}

/**
 * Narrow-width fallback for Throughput/WIP trend line charts. Renders a thin
 * single-line sparkline (no axes, no tooltip, no legend) plus the latest
 * value and WoW delta. Used when the card's rendered width is below the
 * sparkline threshold — the multi-type line chart is illegible there.
 */
export function TrendSparkline({ data, activeType }: Readonly<ITrendSparklineProps>) {
  if (data.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        No data for this period.
      </p>
    );
  }

  const series = data.map((p) => ({ week: p.week, value: totalForPoint(p, activeType) }));
  const last = series.at(-1)?.value ?? 0;
  const prev = series.at(-2)?.value ?? null;
  const delta = prev === null ? null : last - prev;

  let deltaLabel: React.ReactNode;
  if (delta === null) {
    deltaLabel = <span className="text-muted-foreground">—</span>;
  } else if (delta === 0) {
    deltaLabel = <span className="text-muted-foreground">0 WoW</span>;
  } else {
    const up = delta > 0;
    deltaLabel = (
      <span className={up ? 'text-success' : 'text-error'}>
        {up ? '▲' : '▼'} {Math.abs(delta)} WoW
      </span>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="text-muted-foreground">This week</span>
        <span className="flex items-baseline gap-2">
          <span className="text-base font-semibold tabular-nums text-foreground">{last}</span>
          {deltaLabel}
        </span>
      </div>
      <div className="h-10 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 2, right: 0, bottom: 2, left: 0 }}>
            <Line
              type="monotone"
              dataKey="value"
              stroke="var(--color-primary)"
              strokeWidth={1.5}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
